import React, { useEffect, useRef, useState } from "react";
import { config } from "../../config";

/* ================= GOOGLE MAPS LOADER ================= */

const loadGoogleMaps = () =>
    new Promise((resolve) => {
        if (
            window.google?.maps &&
            window.google.maps.geometry &&
            window.google.maps.drawing
        ) {
            return resolve();
        }

        const existing = document.getElementById("google-maps-script");
        if (existing) {
            existing.addEventListener("load", resolve);
            return;
        }

        const script = document.createElement("script");
        script.id = "google-maps-script";
        script.src =
            `https://maps.googleapis.com/maps/api/js` +
            `?key=${config.mapApiKey}` +
            `&libraries=geometry,drawing`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;

        document.body.appendChild(script);
    });

/* ================= GEOMETRY ================= */

const linesIntersect = (a, b, c, d) => {
    const ccw = (p1, p2, p3) =>
        (p3.lat - p1.lat) * (p2.lng - p1.lng) >
        (p2.lat - p1.lat) * (p3.lng - p1.lng);

    return (
        ccw(a, c, d) !== ccw(b, c, d) &&
        ccw(a, b, c) !== ccw(a, b, d)
    );
};

const isSelfIntersecting = (path) => {
    const pts = [];
    for (let i = 0; i < path?.getLength(); i++) {
        const p = path.getAt(i);
        pts.push({ lat: p.lat(), lng: p.lng() });
    }

    if (pts.length < 4) return false;

    for (let i = 0; i < pts.length; i++) {
        const a1 = pts[i];
        const a2 = pts[(i + 1) % pts.length];

        for (let j = i + 1; j < pts.length; j++) {
            if (
                Math.abs(i - j) <= 1 ||
                (i === 0 && j === pts.length - 1)
            )
                continue;

            const b1 = pts[j];
            const b2 = pts[(j + 1) % pts.length];

            if (linesIntersect(a1, a2, b1, b2)) return true;
        }
    }

    return false;
};

const clonePath = (path) =>
    Array.from({ length: path?.getLength() }, (_, i) => {
        const p = path.getAt(i);
        return { lat: p.lat(), lng: p.lng() };
    });

/* ================= COMPONENT ================= */

const CityMap = ({ boundaries = [], onSave }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const polygonRef = useRef(null);
    const drawingManagerRef = useRef(null);

    const listenersRef = useRef([]);
    const lastValidPathRef = useRef(null);
    const originalBoundaryRef = useRef([]);

    const isRevertingRef = useRef(false);
    const isDrawingRef = useRef(false);

    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    /* ================= HELPERS ================= */

    const clearListeners = () => {
        listenersRef.current.forEach((l) => l.remove());
        listenersRef.current = [];
    };

    /**
     *  CRASH-FREE revert:
     * - NEVER call path.clear()
     * - ONLY mutate existing indices with setAt
     */
    const restorePathSafely = (path, points) => {
        isRevertingRef.current = true;

        // restore existing vertices
        for (let i = 0; i < points.length; i++) {
            if (i < path?.getLength()) {
                path.setAt(
                    i,
                    new window.google.maps.LatLng(points[i].lat, points[i].lng)
                );
            }
        }

        // remove extra points safely (only if Google added temp ones)
        while (path?.getLength() > points.length) {
            path.removeAt(path?.getLength() - 1);
        }

        setTimeout(() => {
            isRevertingRef.current = false;
        }, 0);
    };

    const validateEdit = () => {
        if (isDrawingRef.current) return;
        if (isRevertingRef.current) return;
        if (!polygonRef.current) return;

        const path = polygonRef.current.getPath();
        const snapshot = clonePath(path);

        if (isSelfIntersecting(path)) {
            restorePathSafely(path, lastValidPathRef.current);
            return;
        }

        lastValidPathRef.current = snapshot;
        setIsDirty(true);
    };

    /* ================= SAVE ================= */

    const extractBoundary = () => {
        const points = clonePath(polygonRef.current.getPath()).map((p) => ({
            latitude: p.lat,
            longitude: p.lng,
        }));

        // explicitly close polygon for backend
        const first = points[0];
        const last = points[points.length - 1];

        if (
            first.latitude !== last.latitude ||
            first.longitude !== last.longitude
        ) {
            points.push({ ...first });
        }

        return points;
    };

    /* ================= MAP EFFECT ================= */

    useEffect(() => {
        let mounted = true;

        loadGoogleMaps().then(() => {
            if (!mounted || !mapRef.current) return;

            if (!mapInstance.current) {
                mapInstance.current = new window.google.maps.Map(mapRef.current, {
                    center: { lat: 22.5726, lng: 88.3639 },
                    zoom: 12,
                });
            }

            if (polygonRef.current) {
                clearListeners();
                polygonRef.current.setMap(null);
                polygonRef.current = null;
            }

            /* -------- EDIT EXISTING -------- */
            if (boundaries.length >= 3) {
                originalBoundaryRef.current = boundaries.map((b) => ({ ...b }));

                polygonRef.current = new window.google.maps.Polygon({
                    paths: boundaries.map((b) => ({
                        lat: b.latitude,
                        lng: b.longitude,
                    })),
                    editable: isEditing,
                    draggable: isEditing,
                    strokeColor: isEditing ? "#dc3545" : "#0d6efd",
                    fillColor: isEditing ? "#dc3545" : "#0d6efd",
                    fillOpacity: 0.25,
                });

                polygonRef.current.setMap(mapInstance.current);

                const bounds = new window.google.maps.LatLngBounds();
                boundaries.forEach((b) =>
                    bounds.extend({ lat: b.latitude, lng: b.longitude })
                );
                mapInstance.current.fitBounds(bounds);

                lastValidPathRef.current = clonePath(
                    polygonRef.current.getPath()
                );

                const path = polygonRef.current.getPath();
                listenersRef.current.push(
                    path.addListener("set_at", validateEdit),
                    path.addListener("insert_at", validateEdit),
                    path.addListener("remove_at", validateEdit)
                );
            }

            /* -------- CREATE NEW -------- */
            if (boundaries.length === 0 && isEditing) {
                isDrawingRef.current = true;

                drawingManagerRef.current =
                    new window.google.maps.drawing.DrawingManager({
                        drawingMode:
                            window.google.maps.drawing.OverlayType.POLYGON,
                        drawingControl: false,
                        polygonOptions: {
                            editable: true,
                            draggable: true,
                            strokeColor: "#dc3545",
                            fillColor: "#dc3545",
                            fillOpacity: 0.25,
                        },
                    });

                drawingManagerRef.current.setMap(mapInstance.current);

                window.google.maps.event.addListener(
                    drawingManagerRef.current,
                    "overlaycomplete",
                    (e) => {
                        drawingManagerRef.current.setMap(null);
                        polygonRef.current = e.overlay;
                        isDrawingRef.current = false;

                        lastValidPathRef.current = clonePath(
                            polygonRef.current.getPath()
                        );

                        const path = polygonRef.current.getPath();
                        listenersRef.current.push(
                            path.addListener("set_at", validateEdit),
                            path.addListener("insert_at", validateEdit),
                            path.addListener("remove_at", validateEdit)
                        );

                        setIsDirty(true);
                    }
                );
            }
        });

        return () => {
            mounted = false;
        };
    }, [boundaries, isEditing]);

    /* ================= ACTIONS ================= */

    const handleSave = () => {
        if (!polygonRef.current) return;

        const path = polygonRef.current.getPath();
        if (path?.getLength() < 3 || isSelfIntersecting(path)) {
            alert("Invalid boundary");
            return;
        }

        setIsEditing(false);
        setIsDirty(false);
        onSave?.(extractBoundary());
    };

    const handleCancel = () => {
        clearListeners();
        if (polygonRef.current) polygonRef.current.setMap(null);

        if (originalBoundaryRef.current.length >= 3) {
            polygonRef.current = new window.google.maps.Polygon({
                paths: originalBoundaryRef.current.map((b) => ({
                    lat: b.latitude,
                    lng: b.longitude,
                })),
                strokeColor: "#0d6efd",
                fillColor: "#0d6efd",
                fillOpacity: 0.25,
            });

            polygonRef.current.setMap(mapInstance.current);
        }

        setIsEditing(false);
        setIsDirty(false);
    };

    /* ================= RENDER ================= */

    return (
        <div className="rounded-xl border bg-white overflow-hidden">
            <div className="flex justify-between px-4 py-2 bg-gray-50">
                <span className="text-lg">
                    {isEditing
                        ? boundaries.length
                            ? "Editing boundary"
                            : "Draw boundary"
                        : "Boundary locked"}
                </span>

                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-1.5 bg-green-800 text-white rounded"
                    >
                        {boundaries.length ? "Edit" : "Create"}
                    </button>
                )}
            </div>

            <div ref={mapRef} style={{ height: 450 }} />

            {isEditing && (
                <div className="flex justify-between px-4 py-3 border-t">
                    <span className="text-sm text-gray-500">
                        Self-intersections are blocked
                    </span>

                    <div className="flex gap-2">
                        <button
                            onClick={handleCancel}
                            className="px-3 py-1.5 border rounded"
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleSave}
                            disabled={!isDirty}
                            className={`px-4 py-1.5 rounded text-white ${isDirty
                                    ? "bg-blue-600"
                                    : "bg-blue-300 cursor-not-allowed"
                                }`}
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CityMap;
